import { ClientTheme } from '../types/theme.types';

/**
 * Convierte un color hex a variaciones de tonos
 */
function generateColorVariations(hexColor: string) {
  // Función auxiliar para convertir hex a RGB
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Función auxiliar para convertir RGB a hex
  const rgbToHex = (r: number, g: number, b: number) => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };

  const rgb = hexToRgb(hexColor);
  if (!rgb) return { regular: hexColor, strong: hexColor, weak: hexColor };

  // Generar versión más oscura (strong)
  const strong = rgbToHex(
    Math.max(0, Math.floor(rgb.r * 0.6)),
    Math.max(0, Math.floor(rgb.g * 0.6)),
    Math.max(0, Math.floor(rgb.b * 0.6))
  );

  // Generar versión más clara (weak)
  const weak = rgbToHex(
    Math.min(255, Math.floor(rgb.r + (255 - rgb.r) * 0.5)),
    Math.min(255, Math.floor(rgb.g + (255 - rgb.g) * 0.5)),
    Math.min(255, Math.floor(rgb.b + (255 - rgb.b) * 0.5))
  );

  return {
    regular: hexColor,
    strong: strong,
    weak: weak
  };
}

/**
 * Genera el objeto de colores para el tema basado en el perfil del cliente
 */
export function generateClientColors(clientTheme: ClientTheme) {
  const primaryVariations = generateColorVariations(clientTheme.primaryColor);
  const secondaryVariations = generateColorVariations(clientTheme.secondaryColor);
  
  // Usar primaryColorWeak del tema si existe, sino usar el generado automáticamente
  const primaryWeak = clientTheme.primaryColorWeak || primaryVariations.weak;

  return {
    primary: {
      text: {
        regular: primaryVariations.regular,
        strong: primaryVariations.strong,
        weak: primaryWeak,
      },
      bg: {
        regular: primaryVariations.regular,
        strong: primaryVariations.strong,
        weak: primaryWeak,
        inverted: '#FFFFFF',
      },
      border: {
        regular: primaryVariations.regular,
        strong: primaryVariations.strong,
        weak: primaryWeak,
        inverted: clientTheme.primaryColor,
      },
      ic: {
        regular: primaryVariations.regular,
        strong: primaryVariations.strong,
        weak: primaryWeak,
      },
    },
    'secondary-1': {
      text: {
        regular: secondaryVariations.regular,
        strong: secondaryVariations.strong,
        weak: secondaryVariations.weak,
      },
      bg: {
        regular: secondaryVariations.regular,
        strong: secondaryVariations.strong,
        weak: secondaryVariations.weak,
      },
      border: {
        regular: secondaryVariations.regular,
        strong: secondaryVariations.strong,
        weak: secondaryVariations.weak,
      },
      ic: {
        regular: secondaryVariations.regular,
        strong: secondaryVariations.strong,
        weak: secondaryVariations.weak,
      },
    },
  };
}

/**
 * Aplica las variables del tema como CSS custom properties
 * Esto permite usar los colores del cliente en cualquier parte de la app
 */
export function applyThemeVariables(theme: ClientTheme): void {
  const root = document.documentElement;
  
  root.style.setProperty('--client-primary-color', theme.primaryColor);
  root.style.setProperty('--client-secondary-color', theme.secondaryColor);
  
  if (theme.backgroundColor) {
    root.style.setProperty('--client-background-color', theme.backgroundColor);
  }
  
  console.log(`✅ Perfil de diseño aplicado: ${theme.name}`);
  console.log(`   Primary: ${theme.primaryColor}`);
  console.log(`   Secondary: ${theme.secondaryColor}`);
  if (theme.backgroundColor) {
    console.log(`   Background: ${theme.backgroundColor}`);
  }
}
